import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput } from "./shared/utils";

const PREVIEW_CHARS = 500;

interface DocHeading {
  text: string;
  level?: number;
}

interface ParsedDocData {
  ok: true;
  title?: string;
  body_text?: string;  // docs_read response field
  body?: string;
  content?: string;
  text?: string;
  headings?: DocHeading[];
  webViewLink?: string;
  url?: string;
}

interface DocViewCardProps {
  part: ToolUIPart;
}

export const DocViewCard = memo(function DocViewCard({ part }: DocViewCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<ParsedDocData>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const data = result.kind === "ok" ? result.data : null;

  const { fullText, preview, overflow } = useMemo(() => {
    const ft = data?.body_text ?? data?.body ?? data?.content ?? data?.text ?? "";
    const p = ft.length > PREVIEW_CHARS
      ? ft.slice(0, PREVIEW_CHARS).trimEnd() + "…"
      : ft;
    const o = Math.max(0, ft.length - PREVIEW_CHARS);
    return { fullText: ft, preview: p, overflow: o };
  }, [data]);

  const title = data?.title ?? "Document";
  const link = data?.webViewLink ?? data?.url;
  const headings = data?.headings?.filter(h => h.text) ?? [];

  const status = isStreaming ? "streaming" : isError ? "error" : "ready";

  return (
    <CardShell
      icon="◻"
      title={title}
      accent={isError ? "error" : "docs"}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__loading">Reading document...</div>
      )}
      {isError && (
        <div className="tool-card__error-msg">{result.kind === "error" ? result.error : "Error"}</div>
      )}
      {!isStreaming && !isError && data && (
        <div className="tool-card__docview-body">
          {headings.length > 0 && (
            <nav aria-label="Document outline">
              <ul role="list" className="tool-card__docview-toc">
                {headings.map((h, i) => (
                  <li
                    key={i}
                    className="tool-card__docview-toc-entry"
                    style={{ paddingLeft: `${((h.level ?? 1) - 1) * 12}px` }}
                    aria-label={`Heading level ${h.level ?? 1}: ${h.text}`}
                  >
                    {h.text}
                  </li>
                ))}
              </ul>
            </nav>
          )}
          {preview ? (
            <div className="tool-card__docview-preview">{preview}</div>
          ) : (
            <div className="tool-card__empty">Document is empty.</div>
          )}
          <div className="tool-card__sheet-footer">
            {overflow > 0 && (
              <span className="tool-card__sheet-overflow">+{overflow} more char{overflow !== 1 ? "s" : ""}</span>
            )}
            {link && (
              <a
                className="tool-card__sheet-link"
                href={link}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`Open ${title} in Docs (opens in new tab)`}
              >
                Open in Docs ↗
              </a>
            )}
          </div>
        </div>
      )}
    </CardShell>
  );
});
